import { FLOOR_PRICE_NO_RULE_ID } from '@pubstack/common/src/pbs/floor-rule'
import { AdmStack, CreateAdmStack } from '@pubstack/common/src/stack'
import { FunctionComponent, useCallback, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import styled from '@emotion/styled'
import { useFloorRules } from '~/api/app/floor-rules.api'
import { Fonts } from '~/assets/style/fonts'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { InputTextWrapper } from '~/components/Input'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Spinner } from '~/components/Spinner'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { displayWithCurrency } from '~/utils/string'
import { AdUnit } from '@pubstack/common/src/adunit'
import { StackFormData } from '../StackEdit'
import { ToggleWithText } from './ToggleWithText'
import { DeviceType } from '@pubstack/common/src/device-type'
import { capitalizeFirstLetter } from '@pubstack/common/src/stringUtils'
import { Colors } from '~/assets/style/colors'
import { ChipListInput } from '~/components/ChipListInput'

type AdmFloorPriceSelectionProps = {
  isEditing: boolean
  adUnits: { [key in DeviceType]: AdUnit[] }
  isLoading: boolean
  stack?: AdmStack
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const SpecificRule = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  > div {
    flex: 1
  }
  > ${Button} {
      margin-left: 6px;
    }
`

const ErrorMessage = styled.span`
  ${Fonts.P2};
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  ${Fonts.colors.Alert};
`

const SELECT_FLOOR_RULE_TRACKING_LABEL = (isEditing: boolean, isStandard: boolean) => `adm-${isEditing ? 'update' : 'create'}-stack-select-${isStandard ? 'standard' : 'specific'}-floor-rule`

export const AdmFloorPriceSelection: FunctionComponent<AdmFloorPriceSelectionProps> = ({ adUnits, isEditing, isLoading, stack }) => {
  // State
  const [isFloorToggleChecked, setIsFloorToggleChecked] = useState<boolean>(!!stack?.configuration.floorRuleConfig && !!stack?.configuration.mobileFloorRuleConfig)
  // Resync the toggle from the stack config when the stack changes ("previous value" pattern).
  const [prevStack, setPrevStack] = useState(stack)
  if (stack !== prevStack) {
    setPrevStack(stack)
    setIsFloorToggleChecked(!!stack?.configuration.floorRuleConfig && !!stack?.configuration.mobileFloorRuleConfig)
  }

  // Hooks
  const { data: floorRules, isLoading: floorRulesLoading } = useFloorRules()
  const { setValue } = useFormContext<StackFormData>()

  const currencySymbol = useScopeCurrency()

  const floorRulesOptions: SelectOptionProp<string>[] = [
    ...floorRules.map((l) => ({
      label: `${l.name} (${displayWithCurrency((l.floor / 100).toFixed(2), currencySymbol)})`,
      value: l.id,
      trackingLabel: SELECT_FLOOR_RULE_TRACKING_LABEL(isEditing, false) as TrackingLabel,
    })),
    { label: 'No floor price', value: FLOOR_PRICE_NO_RULE_ID, trackingLabel: SELECT_FLOOR_RULE_TRACKING_LABEL(isEditing, false) as TrackingLabel },
  ]

  const onToggle = (newValue: boolean) => {
    setIsFloorToggleChecked(newValue)
    if (!newValue) {
      setValue('configuration.floorRuleConfig', undefined)
      setValue('configuration.mobileFloorRuleConfig', undefined)
    }
  }

  return (
    <>
      {floorRulesLoading || isLoading ? (
        <Spinner />
      ) : (
        <Wrapper>
          <ToggleWithText
            checked={isFloorToggleChecked}
            onChange={onToggle}
            title="Floor price"
            trackingLabel={isFloorToggleChecked ? `adm-${isEditing ? 'update' : 'create'}-floor-rule-unset` : `adm-${isEditing ? 'update' : 'create'}-floor-rule-set`}
          />
          {isFloorToggleChecked && (
            <>
              <div>Apply specific rules for specific ad units. The standard rule will be applied on non selected ad units.</div>
              <AdmFloorPriceDeviceSelection device="desktop" floorRulesOptions={floorRulesOptions} isEditing={isEditing} adUnits={adUnits['desktop']} />
              <AdmFloorPriceDeviceSelection device="mobile" floorRulesOptions={floorRulesOptions} isEditing={isEditing} adUnits={adUnits['mobile']} />
            </>
          )}
        </Wrapper>
      )}
    </>
  )
}

const Title = styled.h2`
  ${Fonts.H2};
  ${Colors.Jet};
  font-weight: 500;
`

const FloorSelection = styled.div`

  display: flex;
  flex-direction: column;
  gap: 1rem;
  ${InputTextWrapper} {
      width: 450px;
    }

    & > div {
        display: flex;
        gap: 12px;
      }
`

type KeyOfTypeInto<Structure, TypeSeeked> = keyof {
  [Field in keyof Structure as [Structure[Field]] extends [TypeSeeked] ? Field : never]: any
}
type StackConfigurationFloorFormFields = KeyOfTypeInto<StackFormData['configuration'], CreateAdmStack['configuration']['floorRuleConfig']>

const CONFIG_PROPERTY_BY_DEVICE: Record<DeviceType, StackConfigurationFloorFormFields> = {
  desktop: 'floorRuleConfig',
  mobile: 'mobileFloorRuleConfig',
}
type AdmFloorPriceDeviceSelectionProps = {
  device: DeviceType
  floorRulesOptions: SelectOptionProp<string>[]
  isEditing: boolean
  adUnits: AdUnit[]
}
const AdmFloorPriceDeviceSelection: FunctionComponent<AdmFloorPriceDeviceSelectionProps> = ({ device, floorRulesOptions, isEditing, adUnits }) => {
  const fieldName = CONFIG_PROPERTY_BY_DEVICE[device]

  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext<StackFormData>()
  const floorRuleConfig = useWatch({ control, name: `configuration.${fieldName}` })
  const { fields, append, remove } = useFieldArray({
    control,
    name: `configuration.${fieldName}.specificFloorRuleConfigs`,
    rules: {
      validate: {
        value: (formValue) => {
          return !!formValue && formValue.some((f) => f.adUnitIds.length === 0) ? 'Every rule must be linked to at least one ad unit.' : true
        },
      },
    },
  })

  const availableAdUnits = adUnits
    .filter((adUnit) => adUnit.devices.includes(device))
    .map((adUnit) => ({
      label: adUnit.name,
      value: adUnit.name,
      trackingLabel: `adm-sites-stacks-form-${fieldName}-${device}-ad-unit-set` as TrackingLabel,
    }))

  const remainingAdUnits = availableAdUnits.filter((adUnit) => floorRuleConfig?.specificFloorRuleConfigs?.find((field) => field.adUnitIds.includes(adUnit.value)) === undefined)

  const getRemainingFloorRulesOptions = useCallback(
    (selectedFloorRuleId?: string) => {
      const usedFloorRuleIds = fields.map((field) => field.floorRuleId)
      if (floorRuleConfig?.standardFloorRuleId) {
        usedFloorRuleIds.push(floorRuleConfig.standardFloorRuleId)
      }
      return floorRulesOptions.filter((option) => !usedFloorRuleIds.includes(option.value) || option.value === selectedFloorRuleId)
    },
    [fields, floorRuleConfig, floorRulesOptions]
  )

  return (
    <FloorSelection>
      <Title>{capitalizeFirstLetter(device)}</Title>
      <Select
        options={getRemainingFloorRulesOptions(floorRuleConfig?.standardFloorRuleId)}
        control={control}
        name={`configuration.${fieldName}.standardFloorRuleId`}
        label="Standard rule"
        rules={{
          required: 'You must select a rule.',
        }}
      />
      {errors.configuration?.[fieldName]?.standardFloorRuleId?.message && (
        <ErrorMessage>
          <Icon name={'alert'} width={'0.875rem'} /> {errors.configuration[fieldName].standardFloorRuleId.message}
        </ErrorMessage>
      )}
      {fields.map((field, index) => {
        return (
          <SpecificRule key={field.id}>
            <Select
              options={getRemainingFloorRulesOptions(field.floorRuleId)}
              control={control}
              name={`configuration.${fieldName}.specificFloorRuleConfigs.${index}.floorRuleId`}
              rules={{
                required: 'You must select a rule.',
              }}
              label={`Specific rule`}
            />
            <ChipListInput
              isSpaceAllowed
              label={'Apply rule to'}
              control={control}
              name={`configuration.${fieldName}.specificFloorRuleConfigs.${index}.adUnitIds`}
              selectableOptions={remainingAdUnits}
              onChange={() => {
                trigger()
              }}
            />

            <Button variant={'tertiary'} iconName={'delete'} onClick={() => remove(index)} trackingLabel={`adm-${isEditing ? 'update' : 'create'}-floor-rule-specific-delete`} />
          </SpecificRule>
        )
      })}
      <div>
        <Button
          variant="secondary"
          trackingLabel={`adm-${isEditing ? 'update' : 'create'}-floor-rule-specific-new`}
          disabled={getRemainingFloorRulesOptions().length === 0 || remainingAdUnits.length === 0}
          onClick={() => {
            append({ floorRuleId: getRemainingFloorRulesOptions()?.[0]?.value, adUnitIds: [] })
          }}
        >
          Add specific rule
        </Button>
      </div>
      {errors.configuration?.[fieldName]?.specificFloorRuleConfigs?.root?.message && (
        <ErrorMessage>
          <Icon name={'alert'} width={'0.875rem'} /> {errors.configuration[fieldName].specificFloorRuleConfigs.root.message}
        </ErrorMessage>
      )}
    </FloorSelection>
  )
}
